/**
 * PrizeWheel Component
 *
 * Displays a spinning prize wheel animation and reveals the selected prize.
 * Shows when winner has captured the bonus piece for both players.
 * Prizes include all entries from both players (up to 6 total).
 *
 * Props:
 * - prizes: Array of all prize strings from both players to display
 * - isSpinning: Boolean indicating if wheel is currently spinning
 * - selectedPrize: String of the selected prize (null during spin)
 * - onSpinComplete: Callback when spin animation completes
 * - spinDuration: Duration of spin in milliseconds (default 5000)
 * - waitingForSpin: Boolean indicating if waiting for winner to click spin button
 * - isWinner: Boolean indicating if current player is the winner
 * - onSpinClick: Callback when winner clicks spin button
 */

import { useEffect, useRef, useState } from 'react';
import { COPY } from '../copy';

export default function PrizeWheel({
  prizes = [],
  isSpinning = false,
  selectedPrize = null,
  onSpinComplete = null,
  spinDuration = 5000,
  waitingForSpin = false,
  isWinner = false,
  onSpinClick = null,
}) {
  const spinTimerRef = useRef(null);
  const [glowingIndex, setGlowingIndex] = useState(0);

  // Handle glowing animation - cycle through prizes during spin
  useEffect(() => {
    if (isSpinning && prizes.length > 0) {
      const intervalDuration = 200; // 200ms per prize glow
      const glowInterval = setInterval(() => {
        setGlowingIndex((prev) => (prev + 1) % prizes.length);
      }, intervalDuration);

      return () => clearInterval(glowInterval);
    }
  }, [isSpinning, prizes.length]);

  // Handle spin completion timer
  useEffect(() => {
    if (isSpinning && onSpinComplete) {
      spinTimerRef.current = setTimeout(() => {
        onSpinComplete();
      }, spinDuration);

      return () => {
        if (spinTimerRef.current) {
          clearTimeout(spinTimerRef.current);
        }
      };
    }
  }, [isSpinning, onSpinComplete, spinDuration]);

  // Don't render if not spinning, no selected prize, and not waiting for spin
  if (!isSpinning && !selectedPrize && !waitingForSpin) {
    return null;
  }

  // Don't render if no prizes
  if (!Array.isArray(prizes) || prizes.length === 0) {
    return null;
  }

  return (
    <div
      data-testid="prize-wheel"
      role="status"
      aria-live="polite"
      className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50"
    >
      <div className="bg-dark-charcoal border-2 border-neon-purple shadow-glow-purple rounded-lg p-8 max-w-md w-full mx-4 text-center">
        {waitingForSpin ? (
          <>
            {/* Waiting for Spin State */}
            <h2 className="text-3xl font-bold text-neon-purple text-glow-purple mb-6">
              {isWinner ? COPY.prizes.wheel.titles.ready : COPY.prizes.wheel.titles.readyAlt}
            </h2>

            <div className="text-6xl mb-6">🎰</div>

            {/* Prize List */}
            <div className="space-y-2 mb-6">
              <p className="text-lg font-semibold text-neon-cyan mb-3">
                {COPY.prizes.wheel.labels.prizePool}
              </p>
              {prizes.map((prize, index) => (
                <div
                  key={index}
                  className="bg-dark-purple border border-neon-purple/50 text-gray-200 py-2 px-4 rounded-lg"
                >
                  {prize}
                </div>
              ))}
            </div>

            {/* Spin Button or Waiting Message */}
            {isWinner ? (
              <button
                onClick={onSpinClick}
                className="px-8 py-3 bg-neon-purple hover:bg-neon-pink border-2 border-white/70 hover:border-white/70 text-white font-bold text-xl rounded-lg transition-all shadow-glow-purple"
              >
                {COPY.prizes.wheel.buttons.spin}
              </button>
            ) : (
              <p className="text-lg text-neon-cyan animate-pulse">
                {COPY.prizes.wheel.waiting}
              </p>
            )}
          </>
        ) : isSpinning ? (
          <>
            {/* Spinning State */}
            <h2 className="text-3xl font-bold text-neon-purple text-glow-purple mb-6">
              {COPY.prizes.wheel.titles.spinning}
            </h2>

            <div className="text-6xl mb-6">🎰</div>

            {/* Prize List with Glowing Animation */}
            <div className="space-y-2">
              <p className="text-lg font-semibold text-neon-cyan mb-3">
                {COPY.prizes.wheel.labels.deciding}
              </p>
              {prizes.map((prize, index) => (
                <div
                  key={index}
                  className={`py-2 px-4 rounded-lg transition-all duration-200 ${
                    index === glowingIndex
                      ? 'bg-neon-yellow text-dark-navy border-2 border-neon-yellow shadow-glow-yellow font-bold scale-105'
                      : 'bg-dark-purple border border-neon-purple/50 text-gray-200'
                  }`}
                >
                  {prize}
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            {/* Selected Prize Display */}
            <h2 className="text-3xl font-bold text-neon-green text-glow-cyan mb-4">
              {COPY.prizes.wheel.titles.complete}
            </h2>

            <div className="text-6xl mb-6">🎉</div>

            <p className="text-xl text-neon-cyan mb-4">{COPY.prizes.wheel.result.announcement}</p>

            <div className="bg-gradient-to-r from-neon-yellow to-neon-coral border-2 border-neon-yellow text-dark-navy py-4 px-6 rounded-lg text-2xl font-bold mb-6 shadow-glow-yellow animate-pulse">
              {selectedPrize}
            </div>

            <p className="text-gray-300">
              {isWinner
                ? COPY.prizes.wheel.result.winner
                : COPY.prizes.wheel.result.loser}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
